import {
  Fragment,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { useNavigate, useSearchParams } from "react-router";
import { formatText, useTexts, type Locale } from "../texts";
import { ChatIcon } from "../ui/icons/Icon";
import { TaskWindow } from "../ui/TaskWindow";
import { RangeCalendar, rangeText } from "../ui/RangeCalendar";
import { FormActions } from "../ui/kit";
import { Sheet as SharedSheet } from "../ui/Sheet";
import styles from "./ai.module.css";

// Piezas de las pantallas de IA (R2) con el catálogo 1.14.7: fila de filtros C07 con «Más filtros» (panel en escritorio,
// hoja en el celular), período C07, buscador C06, indicador C32, tabla C28 que en el celular es lista C17, menú ⋮ C11,
// ventana C30 y esqueletos C22 de la misma vista. Todo filtro vive en la dirección (E84).

/** Celular (≤ 767 px), escuchando los cambios de ancho. */
export function useIsMobile() {
  const query = "(max-width: 767px)";
  const [mobile, setMobile] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const change = () => setMobile(list.matches);
    list.addEventListener("change", change);
    return () => list.removeEventListener("change", change);
  }, []);
  return mobile;
}

/** Filtros en la dirección: cambiar uno vuelve a la primera página y no llena el historial. */
export function useFilters() {
  const [params, setParams] = useSearchParams();
  const get = (key: string) => params.get(key) ?? "";
  const set = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    next.delete("page");
    setParams(next, { replace: true });
  };
  return { params, get, set };
}

/**
 * C27 1.14.9 · la hoja compartida (RC1 · K1) con lo de adentro de las pantallas de IA: opciones de 48 px, chips y
 * botones de «Más filtros» a todo el ancho.
 */
function Sheet(props: {
  label: string;
  role?: "dialog" | "menu";
  sheetRef?: RefObject<HTMLDivElement | null>;
  onClose: () => void;
  children: ReactNode;
}) {
  return <SharedSheet {...props} className={styles.sheet} />;
}

/** Subtítulo de una sección con su línea y su acción a la derecha. */
export function Section({
  id,
  title,
  line,
  action,
  children,
}: {
  id: string;
  title: string;
  line?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={styles.section} aria-labelledby={id}>
      <div className={styles.sectionHead}>
        <div>
          <h2 id={id} className={styles.subtitle}>
            {title}
          </h2>
          {line ? <p className={styles.sectionLine}>{line}</p> : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/** C32 · fila de indicadores (2 por fila en el celular). */
export function Indicators({ label, children, count }: { label: string; children: ReactNode; count?: number }) {
  return (
    <div
      className={styles.indicators}
      style={{ ["--cols" as string]: String(count ?? 4) }}
      role="group"
      aria-label={label}
      data-part="indicadores"
    >
      {children}
    </div>
  );
}

/** C32 · indicador: nombre, valor y contexto. Con enlace, toda la tarjeta lo lleva; sin dato, «—» con el motivo. */
export function Indicator({
  label,
  value,
  line,
  href,
  small,
  muted,
  icon,
}: {
  label: string;
  value: string;
  line?: ReactNode;
  href?: string;
  small?: boolean;
  muted?: boolean;
  /** Nombre del ícono (ChatIcon) antes del nombre, D15: Capacidades de IA. */
  icon?: string;
}) {
  const body = (
    <>
      <span className={styles.kpiLabel}>
        {icon ? <ChatIcon name={icon} className={styles.kpiIcon} /> : null}
        {label}
      </span>
      <span className={`${styles.kpiValue}${small ? ` ${styles.kpiSmall}` : ""}${muted ? ` ${styles.kpiMuted}` : ""}`}>
        {value}
      </span>
      {line ? <span className={styles.kpiLine}>{line}</span> : null}
    </>
  );
  return href ? (
    <a className={`${styles.kpi} ${styles.kpiLink}`} href={href} data-part="indicador">
      {body}
    </a>
  ) : (
    <div className={styles.kpi} data-part="indicador">
      {body}
    </div>
  );
}

/** C22 · esqueleto de indicadores con la misma cuadrícula. */
export function IndicatorSkeleton({ count, label }: { count: number; label: string }) {
  return (
    <Indicators label={label} count={count}>
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className={styles.kpi} aria-hidden="true">
          <span className={styles.bone} style={{ width: "50%" }} />
          <span className={styles.bone} style={{ width: "35%", height: 24 }} />
          <span className={styles.bone} style={{ width: "70%" }} />
        </div>
      ))}
    </Indicators>
  );
}

/** C22 · esqueleto de filas de lista (C17) o de tabla (C28), con su alto. */
export function RowsSkeleton({ rows = 4, table }: { rows?: number; table?: boolean }) {
  return (
    <div className={table ? styles.skeletonTable : styles.skeletonList} role="status" aria-busy="true">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className={table ? styles.skeletonTableRow : styles.skeletonRow} aria-hidden="true">
          {table ? null : <span className={styles.boneIcon} />}
          <span className={styles.skeletonText}>
            <span className={styles.bone} style={{ width: `${45 + ((index * 13) % 30)}%` }} />
            <span className={styles.bone} style={{ width: `${30 + ((index * 7) % 25)}%` }} />
          </span>
        </div>
      ))}
    </div>
  );
}

/** C06 · buscador: filtra 50 ms después de la última tecla; × o Esc lo vacían al instante. */
export function SearchBox({
  id,
  value,
  placeholder,
  onChange,
}: {
  id: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  const { L } = useTexts();
  const [text, setText] = useState(value);
  const [seen, setSeen] = useState(value);
  const timer = useRef<number | null>(null);
  const input = useRef<HTMLInputElement>(null);
  // Si la dirección cambia desde afuera (Limpiar búsqueda, atrás), el campo la sigue.
  if (seen !== value) {
    setSeen(value);
    setText(value);
  }
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );
  const change = (next: string, now = false) => {
    setText(next);
    if (timer.current) window.clearTimeout(timer.current);
    if (now || next === "") onChange(next.trim());
    else timer.current = window.setTimeout(() => onChange(next.trim()), 50);
  };
  return (
    <label className={`tm-search ${styles.search}`} htmlFor={id} data-part="buscador">
      <ChatIcon name="search" />
      <span className="tm-sr">{placeholder}</span>
      <input
        id={id}
        ref={input}
        className="bheli-composite-input"
        type="search"
        enterKeyHint="search"
        autoComplete="off"
        placeholder={placeholder}
        value={text}
        onChange={(event) => change(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape" && text) {
            event.preventDefault();
            change("", true);
          }
        }}
      />
      {text ? (
        <button
          type="button"
          className="tm-icon-btn"
          aria-label={L("ADM_AI_CLEAR_SEARCH")}
          title={L("ADM_AI_CLEAR_SEARCH")}
          onClick={() => {
            change("", true);
            input.current?.focus();
          }}
        >
          <ChatIcon name="x" />
        </button>
      ) : null}
    </label>
  );
}

/** C07 · fila de filtros: no salta de línea; en el celular se desliza de lado con un desvanecido. */
export function FilterRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.filterRow} role="group" aria-label={label} data-part="filtros">
      {children}
    </div>
  );
}

/** C07 · píldora (interruptor o excluyente) con su estado en aria-pressed. */
export function Pill({
  pressed,
  onClick,
  children,
  icon,
  label,
}: {
  pressed: boolean;
  onClick: () => void;
  children: ReactNode;
  icon?: string;
  label?: string;
}) {
  return (
    <button
      type="button"
      className={`tm-pill ${styles.pill}`}
      aria-pressed={pressed ? "true" : "false"}
      aria-label={label}
      onClick={onClick}
    >
      {icon ? <ChatIcon name={icon} /> : null}
      {children}
    </button>
  );
}

/** C07 · línea de 1 × 20 px entre grupos de píldoras. */
export const PillDivider = () => <span className={styles.pillDivider} aria-hidden="true" />;

export type FilterGroup = {
  key: string;
  title: string;
  options: { value: string; label: string }[];
  /** Grupos que crecen (empresas): buscador C06 sobre las opciones, que trae quien lo usa. */
  search?: { id: string; placeholder: string; value: string; onChange: (value: string) => void };
};

/**
 * C07 · «Más filtros ▾» al final de la fila: panel con chips por grupo (una opción o ninguna por grupo), «Limpiar
 * filtros» y «Aplicar». En escritorio, bajo la fila; en el celular, hoja inferior. Lo aplicado se ve en la fila.
 */
export function MoreFilters({
  groups,
  value,
  onApply,
}: {
  groups: FilterGroup[];
  value: Record<string, string>;
  onApply: (next: Record<string, string>) => void;
}) {
  const { L } = useTexts();
  const mobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>(value);
  const panel = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!panel.current?.contains(event.target as Node) && !trigger.current?.contains(event.target as Node))
        setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  // RC2 · en escritorio el panel (560 px) nace en el borde izquierdo del botón; si no cabe a la derecha (el botón está
  // lejos, como en Problemas a 1440 px), se corre a la izquierda lo justo para quedar a 16 px del borde (E83).
  useLayoutEffect(() => {
    const node = panel.current;
    if (!open || mobile || !node) return;
    node.style.removeProperty("left");
    const box = node.getBoundingClientRect();
    const overflow = box.right - (document.documentElement.clientWidth - 16);
    if (overflow > 0) node.style.left = `${-Math.min(overflow, Math.max(0, box.left - 16))}px`;
  }, [open, mobile]);
  const toggle = () => {
    setDraft(value);
    setOpen((current) => !current);
  };
  const apply = (next: Record<string, string>) => {
    onApply(next);
    setOpen(false);
    trigger.current?.focus();
  };
  const content = (
    <>
      <div className={styles.panelGroups}>
        {groups.map((group) => (
          <div key={group.key} className={styles.panelGroup} role="group" aria-labelledby={`${panelId}-${group.key}`}>
            <span id={`${panelId}-${group.key}`} className={styles.panelTitle}>
              {group.title}
            </span>
            {group.search ? (
              <SearchBox
                id={group.search.id}
                value={group.search.value}
                placeholder={group.search.placeholder}
                onChange={group.search.onChange}
              />
            ) : null}
            <div className="tm-pills">
              {group.options.map((option) => (
                <Pill
                  key={option.value}
                  pressed={draft[group.key] === option.value}
                  onClick={() =>
                    setDraft((current) => ({
                      ...current,
                      [group.key]: current[group.key] === option.value ? "" : option.value,
                    }))
                  }
                >
                  {option.label}
                </Pill>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className={styles.panelActions}>
        <button
          type="button"
          className="tm-btn secondary"
          onClick={() => apply(Object.fromEntries(groups.map((group) => [group.key, ""])))}
        >
          {L("ADM_AI_CLEAR_FILTERS")}
        </button>
        <button type="button" className="tm-btn primary" onClick={() => apply(draft)}>
          {L("ADM_AI_APPLY")}
        </button>
      </div>
    </>
  );
  return (
    <div className={styles.moreHost}>
      <button
        ref={trigger}
        type="button"
        className={`tm-pill ${styles.pill}`}
        aria-expanded={open ? "true" : "false"}
        aria-controls={open ? panelId : undefined}
        aria-haspopup="dialog"
        onClick={toggle}
        data-part="mas-filtros"
      >
        {L("ADM_AI_MORE_FILTERS")}
        <ChatIcon name="chevron-down" />
      </button>
      {open ? (
        mobile ? (
          <Sheet label={L("ADM_AI_MORE_FILTERS")} sheetRef={panel} onClose={() => setOpen(false)}>
            {content}
          </Sheet>
        ) : (
          <div ref={panel} id={panelId} className={styles.panel} role="dialog" aria-label={L("ADM_AI_MORE_FILTERS")}>
            {content}
          </div>
        )
      ) : null}
    </div>
  );
}

/** Períodos de Uso y costos (la dirección guarda el código; un rango es «aaaa-mm-dd..aaaa-mm-dd»). */
export const periodPresets = ["today", "7d", "30d", "90d"] as const;

/** Nombre de un período: «Últimos 30 días» o el rango «17 set. – 16 oct.» (C07, C34). */
export function periodLabel(L: (key: string) => string, period: string, locale: Locale = "es-PE"): string {
  if ((periodPresets as readonly string[]).includes(period)) return L(`ADM_AI_PERIOD_${period.toUpperCase()}`);
  const [from, to] = period.split("..");
  return from && to ? rangeText(locale, from, to) : L("ADM_AI_PERIOD_30D");
}

/** dd/mm/aaaa → aaaa-mm-dd, o null si no es una fecha. */
export function parseDay(text: string): string | null {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text.trim());
  if (!match) return null;
  const [, d, m, y] = match.map(Number) as [number, number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/**
 * C07 · período: píldora encendida con calendar que abre un menú con los períodos y «Elegir fechas» (Desde y Hasta
 * vacíos hasta que la persona escribe, E90). Nunca el selector de fechas del navegador (E8).
 */
export function PeriodPill({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { L, locale } = useTexts();
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  // Un rango aplicado («aaaa-mm-dd..aaaa-mm-dd») abre el calendario con esos días; un período, sin días elegidos.
  const applied = value.includes("..") ? (value.split("..") as [string, string]) : null;
  const host = useRef<HTMLDivElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const mobile = useIsMobile();
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) =>
      !host.current?.contains(event.target as Node) && !sheet.current?.contains(event.target as Node) && setOpen(false);
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const choose = (next: string) => {
    onChange(next);
    setOpen(false);
    setCustom(false);
    trigger.current?.focus();
  };
  return (
    <div className={styles.moreHost} ref={host}>
      <button
        ref={trigger}
        type="button"
        className={`tm-pill ${styles.pill}`}
        aria-pressed="true"
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        aria-label={`${L("ADM_AI_PERIOD")}: ${periodLabel(L, value, locale)}`}
        onClick={() => {
          setOpen((current) => !current);
          setCustom(false);
        }}
        data-part="periodo"
      >
        <ChatIcon name="calendar" />
        {periodLabel(L, value, locale)}
        <ChatIcon name="chevron-down" />
      </button>
      {open ? (
        <PeriodBody
          mobile={mobile}
          sheetRef={sheet}
          custom={custom}
          label={L("ADM_AI_PERIOD")}
          onClose={() => setOpen(false)}
        >
          {custom ? (
            // C07/C34 · AR17: el selector de rango del catálogo (dos meses en escritorio, uno en la hoja del celular);
            // nunca fechas escritas a mano.
            <RangeCalendar
              from={applied?.[0] ?? null}
              to={applied?.[1] ?? null}
              months={mobile ? 1 : 2}
              onCancel={() => setCustom(false)}
              onApply={({ from: first, to: last }) => choose(`${first}..${last}`)}
            />
          ) : (
            <>
              {periodPresets.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  role="menuitemradio"
                  aria-checked={value === preset}
                  className={styles.menuItem}
                  onClick={() => choose(preset)}
                >
                  <span>{L(`ADM_AI_PERIOD_${preset.toUpperCase()}`)}</span>
                  {value === preset ? <ChatIcon name="check" /> : null}
                </button>
              ))}
              <button
                type="button"
                role="menuitem"
                className={styles.menuItem}
                onClick={() => {
                  setCustom(true);
                  requestAnimationFrame(() =>
                    (sheet.current ?? host.current)
                      ?.querySelector<HTMLElement>("[data-part=calendario] button")
                      ?.focus(),
                  );
                }}
              >
                <span>{L("ADM_AI_CHOOSE_DATES")}</span>
                <ChatIcon name="calendar" />
              </button>
            </>
          )}
        </PeriodBody>
      ) : null}
    </div>
  );
}

/** Menú del período: bajo la píldora en escritorio, hoja en el celular. */
function PeriodBody({
  mobile,
  sheetRef,
  custom,
  label,
  onClose,
  children,
}: {
  mobile: boolean;
  sheetRef: RefObject<HTMLDivElement | null>;
  custom: boolean;
  label: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return mobile ? (
    <Sheet label={label} role={custom ? "dialog" : "menu"} sheetRef={sheetRef} onClose={onClose}>
      {children}
    </Sheet>
  ) : (
    <div className={styles.menu} role={custom ? "dialog" : "menu"} aria-label={label}>
      {children}
    </div>
  );
}

/** Cómo se lee el orden de una columna (C28): textos A–Z, fechas de la más reciente, números de menor a mayor. */
export type SortKind = "text" | "date" | "number";

export type Column<T> = {
  key: string;
  title: string;
  numeric?: boolean;
  /** Columna que se ordena (un dato por fila): clave del orden en el servidor y cómo se lee. */
  sort?: { key: string; kind: SortKind };
  /** Columna de acciones de la fila: sin título y al ancho de sus botones (C28). */
  actions?: boolean;
  /** Ancho fijo en px (C28 1.14.5): con alguno, la tabla reparte el resto a las columnas sin ancho. */
  width?: number;
  /** Una línea: lo que no entra termina en «…» (el texto completo va en su title). */
  clip?: boolean;
  render: (row: T) => ReactNode;
};

/** Dirección del primer toque en una columna (C28): fechas de la más reciente; textos y números, de menor a mayor. */
export const firstDesc = (kind: SortKind) => kind === "date";

/** El orden en palabras del dato (C28): «A–Z», «Más reciente», «Menor a mayor»… nunca «ascendente». */
export function orderWords(L: (key: string) => string, kind: SortKind, desc: boolean) {
  if (kind === "date") return L(desc ? "C1_ORDER_RECENT" : "C1_ORDER_OLDEST");
  if (kind === "number") return L(desc ? "C1_ORDER_HIGH" : "C1_ORDER_LOW");
  return L(desc ? "C1_ORDER_ZA" : "C1_ORDER_AZ");
}

/**
 * C28 · tabla sin caja: encabezado de 40 px, filas de 56 px, números a la derecha, clic en toda la fila. Con `sort`, las
 * columnas que se ordenan llevan su título como botón y la flecha de 14 px solo en la ordenada; otro toque invierte (el
 * orden lo aplica el servidor, `useSort`). En el celular, lista C17 (`mobile`) con lo principal en dos líneas, el chevron
 * o las acciones de la fila (`end`, ⋮ de 44 px): ninguna tabla a 390 px (E83); el orden va en ⋮ › «Ordenar por».
 */
export function DataTable<T>({
  label,
  columns,
  rows,
  rowKey,
  href,
  mobile,
  sort,
  onSort,
  hoverRows,
}: {
  label: string;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  href?: (row: T) => string | null;
  mobile: (row: T) => { title: ReactNode; tag?: ReactNode; line: ReactNode; end?: ReactNode; lead?: ReactNode };
  sort?: { key: string; desc: boolean } | null;
  onSort?: (next: { key: string; desc: boolean }) => void;
  /** Filas sin destino que igual toman su fondo al pasar el mouse (tienen acciones a la vista, C28 1.14.12). */
  hoverRows?: boolean;
}) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  if (isMobile)
    return (
      <ul className={`tm-list ${styles.mobileList}`} aria-label={label} data-part="lista">
        {rows.map((row) => {
          const view = mobile(row);
          const target = href?.(row) ?? null;
          const inner = (
            <>
              {view.lead ?? null}
              <span className="tm-row-text">
                <span className="tm-line1">
                  <strong className={styles.rowTitle}>{view.title}</strong>
                  {view.tag}
                </span>
                <span className={`tm-line2 ${styles.ellipsis}`}>{view.line}</span>
              </span>
              {target ? (
                <span className="tm-row-end" aria-hidden="true">
                  <ChatIcon name="chevron-right" />
                </span>
              ) : null}
            </>
          );
          return (
            <li key={rowKey(row)} className="tm-row">
              {target ? (
                <a className="tm-row-main" href={target}>
                  {inner}
                </a>
              ) : (
                <div className={`tm-row-main ${styles.staticRow}`}>{inner}</div>
              )}
              {view.end ? <span className={styles.mobileEnd}>{view.end}</span> : null}
            </li>
          );
        })}
      </ul>
    );
  const headClass = (column: Column<T>) =>
    [column.numeric ? styles.numeric : "", column.actions ? styles.actionsCell : "", column.clip ? styles.cellClip : ""]
      .filter(Boolean)
      .join(" ") || undefined;
  const fixed = columns.some((column) => column.width);
  // C28: la columna sin ancho (Capacidades) no baja de 140 px; más angosto, la tabla se desplaza sola.
  const fixedMinWidth = columns.reduce((sum, column) => sum + (column.width ?? 140), 0);
  const table = (
    <table
      className={`${styles.table}${fixed ? ` ${styles.tableFixed}` : ""}`}
      style={fixed ? { minWidth: fixedMinWidth } : undefined}
      aria-label={label}
      data-part="tabla"
    >
      {fixed ? (
        <colgroup>
          {columns.map((column) => (
            <col key={column.key} style={column.width ? { width: column.width } : undefined} />
          ))}
        </colgroup>
      ) : null}
      <thead>
        <tr>
          {columns.map((column) => {
            const order = column.sort;
            if (!order || !onSort)
              return (
                <th key={column.key} scope="col" className={headClass(column)}>
                  {column.title}
                </th>
              );
            const active = sort?.key === order.key;
            return (
              <th
                key={column.key}
                scope="col"
                className={headClass(column)}
                aria-sort={active ? (sort!.desc ? "descending" : "ascending") : "none"}
              >
                <button
                  type="button"
                  className={`${styles.sortButton}${active ? ` ${styles.sortActive}` : ""}`}
                  data-part={active ? "orden-activo" : "orden"}
                  onClick={() => onSort({ key: order.key, desc: active ? !sort!.desc : firstDesc(order.kind) })}
                >
                  {column.title}
                  {active ? <ChatIcon name={sort!.desc ? "arrow-down" : "arrow-up"} /> : null}
                </button>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const target = href?.(row) ?? null;
          return (
            <tr
              key={rowKey(row)}
              className={target ? styles.clickable : hoverRows ? styles.hoverRow : undefined}
              onClick={(event) => {
                if (!target || (event.target as Element).closest("a,button")) return;
                navigate(target);
                window.scrollTo(0, 0);
              }}
            >
              {columns.map((column, index) => (
                <td key={column.key} className={headClass(column)}>
                  {index === 0 && target ? (
                    <a className={styles.rowLink} href={target}>
                      {column.render(row)}
                    </a>
                  ) : (
                    column.render(row)
                  )}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
  return fixed ? <div className={styles.tableScroll}>{table}</div> : table;
}

/** Grupos fijos del ⋮ (C11 1.14.11 (4)): comprobar, cambiar y quitar, en ese orden y separados por una línea. */
export type MenuGroup = "comprobar" | "cambiar" | "quitar";
const menuGroups: (MenuGroup | "")[] = ["", "comprobar", "cambiar", "quitar"];

export type MenuItem = {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  /** Por qué está desactivada, solo cuando no la explica el motivo compartido de su grupo. */
  line?: string;
  danger?: boolean;
  /** Ícono Lucide de 18 px (C02); en un mismo menú no se repite. */
  icon?: string;
  group?: MenuGroup;
};

/** Motivo compartido de un grupo (C11 1.14.11 (6)): una sola línea debajo de sus opciones, con su salida. */
export type MenuReason = { group: MenuGroup; text: string; link?: { label: string; href: string } };

/**
 * C11 · ⋮ con las acciones: menú bajo el botón en escritorio; hoja inferior C27 en el celular, con el mismo contenido. Las
 * opciones van por grupo (comprobar · cambiar · quitar) con una línea de 1 px solo entre grupos que tienen opciones, cada
 * una con su ícono. Una acción desactivada dice por qué: en su línea o, si varias comparten el motivo, una vez debajo del
 * grupo con su salida (E119). ↑ ↓ recorren las opciones activas y la salida; Esc cierra y devuelve el foco al ⋮. Con
 * `reveal`, el ⋮ de una fila de tabla no se ve en reposo: aparece con el hover o el foco de la fila (C28 1.14.11).
 */
export function RowMenu({
  label,
  items,
  title,
  reasons = [],
  reveal,
}: {
  label: string;
  items: MenuItem[];
  title?: string;
  reasons?: MenuReason[];
  reveal?: boolean;
}) {
  const mobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const host = useRef<HTMLDivElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) =>
      !host.current?.contains(event.target as Node) && !sheet.current?.contains(event.target as Node) && setOpen(false);
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    requestAnimationFrame(() =>
      (sheet.current ?? host.current)?.querySelector<HTMLElement>("[role=menuitem]:not([aria-disabled=true])")?.focus(),
    );
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  /** ↑ ↓ Inicio Fin entre las opciones activas y la salida del motivo; las líneas y las desactivadas se saltan. */
  const onMenuKey = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    const options = [...event.currentTarget.querySelectorAll<HTMLElement>("[role=menuitem]:not([aria-disabled=true])")];
    if (options.length === 0) return;
    event.preventDefault();
    const at = options.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? options.length - 1
          : event.key === "ArrowDown"
            ? (at + 1) % options.length
            : (at - 1 + options.length) % options.length;
    options[next]?.focus();
  };
  const groups = menuGroups
    .map((group) => ({
      group,
      items: items.filter((item) => (item.group ?? "") === group),
      reason: reasons.find((reason) => reason.group === group) ?? null,
    }))
    .filter((block) => block.items.length > 0);
  const list = groups.map((block, index) => (
    <Fragment key={block.group || "sin-grupo"}>
      {index > 0 ? <div role="separator" className={styles.menuSep} data-part="separador" /> : null}
      {block.items.map((item) => (
        <button
          key={item.label}
          type="button"
          role="menuitem"
          aria-disabled={item.disabled ? "true" : undefined}
          className={`${styles.menuItem}${item.danger ? ` ${styles.danger}` : ""}`}
          onClick={() => {
            if (item.disabled) return;
            setOpen(false);
            item.onSelect();
          }}
        >
          {item.icon ? (
            <span className={styles.menuIcon} aria-hidden="true">
              <ChatIcon name={item.icon} />
            </span>
          ) : null}
          <span className={styles.menuText}>
            <span>{item.label}</span>
            {item.line ? <small>{item.line}</small> : null}
          </span>
        </button>
      ))}
      {block.reason ? (
        <div className={styles.menuWhy} data-part="motivo">
          <span>{block.reason.text}</span>
          {block.reason.link ? (
            <a role="menuitem" href={block.reason.link.href} onClick={() => setOpen(false)}>
              {block.reason.link.label}
              <ChatIcon name="arrow-right" />
            </a>
          ) : null}
        </div>
      ) : null}
    </Fragment>
  ));
  return (
    <div className={`${styles.moreHost}${reveal ? ` ${styles.reveal}` : ""}`} ref={host}>
      <button
        ref={trigger}
        type="button"
        className={`tm-icon-btn ${styles.dots}`}
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        data-part="mas-acciones"
        onClick={() => setOpen((current) => !current)}
      >
        <ChatIcon name="ellipsis-vertical" />
      </button>
      {open ? (
        mobile ? (
          <Sheet label={title ?? label} role="menu" sheetRef={sheet} onClose={() => setOpen(false)}>
            <div className={styles.menuSheet} onKeyDown={onMenuKey} data-part="menu">
              {title ? <strong className={styles.sheetTitle}>{title}</strong> : null}
              {list}
            </div>
          </Sheet>
        ) : (
          <div
            className={`${styles.menu} ${styles.menuRight}`}
            role="menu"
            aria-label={label}
            data-part="menu"
            onKeyDown={onMenuKey}
          >
            {list}
          </div>
        )
      ) : null}
    </div>
  );
}

/**
 * C30 · ventana (ancha con pasos: 560 px) o C12 confirmación. Esc, × o clic fuera la cierran (con confirmación si hay
 * cambios no aplica: las ventanas de IA no guardan nada hasta su botón). El foco vuelve al botón que la abrió.
 */
export function Window({
  open,
  title,
  onClose,
  children,
  footer,
  wide,
  confirm,
  steps,
  className,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
  wide?: boolean;
  confirm?: boolean;
  steps?: { labels: string[]; current: number };
  /** Clase propia de una ventana (su aire, C30), sin tocar las demás. */
  className?: string;
}) {
  const { L } = useTexts();
  const dialog = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  const close = useRef(onClose);
  const titleId = useId();
  useEffect(() => {
    close.current = onClose;
  });
  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) {
      opener.current = document.activeElement;
      element.showModal();
      requestAnimationFrame(() =>
        (
          element.querySelector<HTMLElement>("[data-autofocus]") ??
          element.querySelector<HTMLElement>(
            confirm
              ? "footer .tm-btn.secondary"
              : ".tm-window-body input, .tm-window-body [role=radio][tabindex='0'], footer .tm-btn.primary",
          )
        )?.focus({ preventScroll: true }),
      );
    } else if (!open && element.open) {
      element.close();
      if (opener.current instanceof HTMLElement && opener.current.isConnected)
        opener.current.focus({ preventScroll: true });
    }
  }, [open, confirm]);
  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    const abort = new AbortController();
    element.addEventListener("cancel", (event) => (event.preventDefault(), close.current()), { signal: abort.signal });
    element.addEventListener(
      "pointerdown",
      (event) => {
        if (event.target !== element) return;
        const r = element.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom)
          close.current();
      },
      { signal: abort.signal },
    );
    return () => abort.abort();
  }, []);
  return (
    <TaskWindow
      ref={dialog}
      className={`tm-window${confirm ? " tm-confirm" : ""} ${wide ? styles.windowWide : styles.windowNormal}${
        className ? ` ${className}` : ""
      }`}
      titleId={titleId}
    >
      <header>
        {confirm ? null : (
          <button type="button" className="tm-back" aria-label={L("ADM_AI_CLOSE")} onClick={onClose}>
            <ChatIcon name="arrow-left" />
          </button>
        )}
        <h2 id={titleId}>{title}</h2>
        {confirm ? null : (
          <button
            type="button"
            className="tm-icon-btn"
            data-tm-close=""
            aria-label={L("ADM_AI_CLOSE")}
            title={L("ADM_AI_CLOSE")}
            onClick={onClose}
          >
            <ChatIcon name="x" />
          </button>
        )}
      </header>
      {steps ? (
        <ol className={`tm-steps ${styles.windowSteps}`} aria-label={L("ADM_AI_STEPS")}>
          {steps.labels.map((label, index) => (
            <li
              key={label}
              className={index < steps.current ? "done" : undefined}
              aria-current={index === steps.current ? "step" : undefined}
            >
              <i>{index < steps.current ? <ChatIcon name="check" /> : index + 1}</i>
              <span>{label}</span>
            </li>
          ))}
        </ol>
      ) : null}
      <div className="tm-window-body">{children}</div>
      <footer>{footer}</footer>
    </TaskWindow>
  );
}

/** C08 · tarjetas con radio para elegir un modelo o un proveedor; una desactivada dice por qué. */
export function RadioCards({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; title: string; line: string; disabled?: boolean }[];
  onChange: (value: string) => void;
}) {
  const enabled = options.filter((option) => !option.disabled);
  const focusValue = enabled.some((option) => option.value === value) ? value : (enabled[0]?.value ?? "");
  const move = (step: number) => {
    const index = enabled.findIndex((option) => option.value === focusValue);
    const next = enabled[(index + step + enabled.length) % enabled.length];
    if (!next) return;
    onChange(next.value);
    requestAnimationFrame(() =>
      document.querySelector<HTMLElement>(`[data-radio-card="${CSS.escape(next.value)}"]`)?.focus(),
    );
  };
  return (
    <div
      className={`tm-options tm-mode-cards ${styles.radioCards}`}
      role="radiogroup"
      aria-label={label}
      onKeyDown={(event) => {
        const step = ({ ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 } as Record<string, number>)[event.key];
        if (!step) return;
        event.preventDefault();
        move(step);
      }}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          className={`tm-option ${styles.radioCard}`}
          data-radio-card={option.value}
          aria-checked={value === option.value ? "true" : "false"}
          aria-disabled={option.disabled ? "true" : undefined}
          tabIndex={option.value === focusValue ? 0 : -1}
          onClick={() => !option.disabled && onChange(option.value)}
        >
          <span className="tm-radio" aria-hidden="true" />
          <span className="tm-option-text">
            <strong>{option.title}</strong>
            <small>{option.line}</small>
          </span>
        </button>
      ))}
    </div>
  );
}

/** «Sin resultados» con la acción que lo resuelve (C18): Borrar búsqueda o Limpiar filtros. */
export function NoResults({ query, things, onClear }: { query: string; things: string; onClear: () => void }) {
  const { L } = useTexts();
  return (
    <div className="tm-empty" role="status" data-part="vacio">
      <strong>{L("ADM_AI_NO_RESULTS")}</strong>
      {query ? <span>{formatText(L("ADM_AI_NO_MATCH"), things, query)}</span> : null}
      <button type="button" className="tm-btn secondary" onClick={onClear}>
        {L(query ? "ADM_AI_CLEAR_SEARCH" : "ADM_AI_CLEAR_FILTERS")}
      </button>
    </div>
  );
}

/** Contador sobre la lista («24 empresas»), del mismo total que el paginado (V24, E100). */
export const Counter = ({ children }: { children: ReactNode }) => (
  <p className={styles.counter} data-part="contador">
    {children}
  </p>
);

export type SortColumn = { key: string; title: string; kind: SortKind };

/**
 * C28 1.14.3 · barra de la tabla: el contador (del mismo total que el paginado, V24) y, en el celular, ⋮ de 44 px que
 * abre la hoja (C27) con «Ordenar por» y debajo cómo está ordenado. Al tocarla, la hoja pasa a ← «Ordenar por»: las
 * columnas que se ordenan con ✓ en la elegida, «Orden» con dos píldoras en palabras del dato y «Aplicar», que recién
 * ordena y cierra; ← o tocar afuera no cambian nada. En escritorio el orden se toca en los títulos de la tabla.
 */
export function TableBar({
  count,
  columns,
  sort,
  onSort,
  hideCount,
}: {
  count: ReactNode;
  columns: SortColumn[];
  sort: { key: string; desc: boolean } | null;
  onSort: (next: { key: string; desc: boolean }) => void;
  /** Celular: solo el ⋮ «Ordenar por», sin contador (la lista de API keys, H9). */
  hideCount?: boolean;
}) {
  const { L } = useTexts();
  const mobile = useIsMobile();
  const [open, setOpen] = useState<"menu" | "sort" | null>(null);
  const [draft, setDraft] = useState<{ key: string; desc: boolean } | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  if (!mobile || columns.length === 0) return <Counter>{count}</Counter>;
  const current = sort ? columns.find((column) => column.key === sort.key) : undefined;
  const chosen = draft ? columns.find((column) => column.key === draft.key) : undefined;
  const close = () => {
    setOpen(null);
    trigger.current?.focus({ preventScroll: true });
  };
  return (
    <div className={`${styles.tableBar}${hideCount ? ` ${styles.tableBarEnd}` : ""}`} data-part="barra">
      {hideCount ? null : <Counter>{count}</Counter>}
      <button
        ref={trigger}
        type="button"
        className={`tm-icon-btn ${styles.dots}`}
        aria-label={L("ADM_TABLE_ACTIONS")}
        title={L("ADM_TABLE_ACTIONS")}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        data-part="mas-acciones"
        onClick={() => setOpen("menu")}
      >
        <ChatIcon name="ellipsis-vertical" />
      </button>
      {open === "menu" ? (
        <Sheet label={L("ADM_TABLE_ACTIONS")} role="menu" onClose={close}>
          <button
            type="button"
            role="menuitem"
            className={styles.menuItem}
            onClick={() => {
              setDraft(sort);
              setOpen("sort");
            }}
          >
            <span className={styles.menuText}>
              <span>{L("C1_SORT_BY")}</span>
              {current ? <small>{`${current.title} · ${orderWords(L, current.kind, sort!.desc)}`}</small> : null}
            </span>
            <ChatIcon name="chevron-right" />
          </button>
        </Sheet>
      ) : open === "sort" ? (
        <Sheet label={L("C1_SORT_BY")} onClose={close}>
          <div className={styles.sheetHead}>
            <button
              type="button"
              className="tm-icon-btn"
              aria-label={L("ADM_AI_BACK")}
              title={L("ADM_AI_BACK")}
              onClick={() => setOpen("menu")}
            >
              <ChatIcon name="arrow-left" />
            </button>
            <strong>{L("C1_SORT_BY")}</strong>
          </div>
          <div role="radiogroup" aria-label={L("C1_SORT_BY")} className={styles.sortChoices}>
            {columns.map((column) => (
              <button
                key={column.key}
                type="button"
                role="radio"
                aria-checked={draft?.key === column.key ? "true" : "false"}
                className={styles.menuItem}
                onClick={() =>
                  setDraft((value) =>
                    value?.key === column.key ? value : { key: column.key, desc: firstDesc(column.kind) },
                  )
                }
              >
                <span>{column.title}</span>
                {draft?.key === column.key ? <ChatIcon name="check" /> : null}
              </button>
            ))}
          </div>
          {chosen ? (
            <div className={styles.sortOrder} role="group" aria-label={L("C1_ORDER_LABEL")}>
              <span className={styles.panelTitle}>{L("C1_ORDER_LABEL")}</span>
              <div className={styles.sortPills}>
                {[firstDesc(chosen.kind), !firstDesc(chosen.kind)].map((desc) => (
                  <Pill
                    key={String(desc)}
                    pressed={draft?.desc === desc}
                    onClick={() => setDraft({ key: chosen.key, desc })}
                  >
                    {orderWords(L, chosen.kind, desc)}
                  </Pill>
                ))}
              </div>
            </div>
          ) : null}
          <div className={styles.sortApply}>
            <button
              type="button"
              className="tm-btn primary"
              disabled={!draft}
              onClick={() => {
                if (draft) onSort(draft);
                close();
              }}
            >
              {L("ADM_AI_APPLY")}
            </button>
          </div>
        </Sheet>
      ) : null}
    </div>
  );
}

/** Inicial de un nombre en su círculo (C17). */
export const Mark = ({ name, tone }: { name: string; tone?: "ok" | "fail" | "warn" }) => (
  <span className={`${styles.mark}${tone ? ` ${styles[`mark_${tone}`]}` : ""}`} aria-hidden="true">
    {name.trim().charAt(0).toUpperCase() || "?"}
  </span>
);

/**
 * C21 · en el celular la acción principal de la pantalla va en la barra fija de abajo (el kit oculta los botones del
 * encabezado a ese ancho). En escritorio no se dibuja: la acción está en el encabezado.
 */
export function MobileActions({ children }: { children: ReactNode }) {
  const mobile = useIsMobile();
  if (!mobile) return null;
  return (
    <div className={styles.mobileOnly}>
      <FormActions>{children}</FormActions>
    </div>
  );
}
